import useSWR, {MutatorCallback} from "swr";
import WatsonApi from "../../backends/WatsonApi";
import {createRef, Ref, useState} from "react";
import FontAwesome from "../../components/utilities/FontAwesome";
import styles from "./styles/DashboardPage.module.scss";
import moment from "moment";
import classNames from 'classnames';
import {handleContentItemClick} from "../../components/blocks/SchoolBlocks/ContentItemBlock/ContentItemBlock";
import {PaginationFooter} from "../SearchPage/PaginationFooter";
import {CSSTransition, TransitionGroup} from "react-transition-group";
import {
    IReactiveEventContentItem,
    IReactiveMediaContentItem,
    IReactiveNewsContentItem,
    ReactiveContentItem,
} from "../../components/schoolfeed/SchoolFeedContentItem";
import ClickableLink from "../../components/utilities/ClickableLink";
import {useStores} from "../../stores/useStores";

const containerClassName = classNames({
    [styles.widget]: true,
    [styles.itemApprovalsContainer]: true,
})

const transitionClassNames = {
    exit: styles.transitionExit,
    exitActive: styles.transitionExitActive,
};



function ApprovalItem(props: {
    item: ContentItemShareRequest,
    boundMutate: MutatorCallback,
    ref: Ref<HTMLTableRowElement>
}) {
    const {userStore} = useStores();

    const [showMessage, setShowMessage] = useState(false);
    const [reviewMessage, setReviewMessage] = useState("");

    async function handleRespond(e, approved: boolean) {
        e.stopPropagation();
        const client = await WatsonApi();
        await client.apis.organizations.organizations_content_item_share_requests_partial_update({
            organization_pk: props.item.target_organization.id,
            id: props.item.id,
            data: {
                reviewer: userStore.id,
                approved,
                review_message: reviewMessage,
            }
        });
        props.boundMutate();
    }

    async function handleApproveAll(e) {
        e.stopPropagation();
        const client = await WatsonApi();
        await client.apis.organizations.organizations_content_item_auto_approval_create({
            organization_pk: props.item.target_organization.id,
            id: props.item.id,
            data: {
                created_by: userStore.id,
                source_organization: props.item.content_item.organization.id,
                target_organization: props.item.target_organization.id,
            }
        });
        props.boundMutate();
    }

    return <>
        <tr ref={props.ref} onClick={() => handleContentItemClick(props.item.content_item as IReactiveEventContentItem | IReactiveNewsContentItem | IReactiveMediaContentItem)}>
            <td title={props.item.content_item.title}>
                <h4>{props.item.content_item.title}</h4>
                {props.item.content_item.description && <p>{props.item.content_item.description}</p>}
            </td>
            <td title={props.item.target_organization.title}>
                <ClickableLink aria-label={"Link to organization page."} onClick={e => e.stopPropagation()} href={`/pages/${props.item.target_organization.id}`}>
                    {props.item.target_organization.title}
                </ClickableLink>
            </td>
            <td title={`${props.item.requester.fname} ${props.item.requester.lname}`}>
                <div>
                    {`${props.item.requester.fname} ${props.item.requester.lname}`}
                </div>
            </td>
            <td title={moment(props.item.created_at).format("MMM D, YYYY")}>{moment(props.item.created_at).format("MMM D, YYYY")}</td>
            <td>
                <button
                    aria-label={"Approve Share Request"}
                    className={`${styles.tooltipVertical} ${styles.reviewButton}`}
                    data-content={"Approve"}
                    onClick={e => handleRespond(e, true)}>
                    <FontAwesome fixedWidth prefix={"fas"} name={"fa-check"}/>
                </button>
                <button
                    aria-label={"Always Approve Share Requests"}
                    className={`${styles.tooltipVertical} ${styles.reviewButton}`}
                    data-content={"Always Approve"}
                    onClick={handleApproveAll}>
                    <FontAwesome fixedWidth prefix={"fas"} name={"fa-check-double"}/>
                </button>
                <button
                    aria-label={"Reject Share Request"}
                    className={`${styles.tooltipVertical} ${styles.reviewButton}`}
                    data-content={"Reject"}
                    onClick={(e) => {
                    e.stopPropagation();
                    setShowMessage(v => !v)
                }}>
                    <FontAwesome fixedWidth prefix={"fas"} name={"fa-times"}/>
                </button>
            </td>
        </tr>
        {showMessage && <tr className={styles.requestConfirm}>
            <td colSpan={5}>
                <textarea
                    onChange={e => setReviewMessage(e.target.value)}
                    placeholder={"Reason for rejection (optional)"}
                >
                    {reviewMessage}
                </textarea>
                <button onClick={e => handleRespond(e, false)}>Submit</button>
            </td>
        </tr>}
    </>

}


export function ContentItemApprovalWidget(props) {
    const {userStore, modalStore} = useStores();

    const [page, setPage] = useState(1);

    const {data, isLoading, mutate} = useSWR(`my-approvals?page=${page}`, async () => {
        const client = await WatsonApi();
        const response = await client.apis.users.users_content_item_share_requests_list({
            user_pk: userStore.id,
            expand: ["content_item", "requester", "target_organization", "content_item.organization"],
            page: page,
            page_size: 10,
            approved__isnull: true,
        })
        const result = JSON.parse(response.data);
        return {
            requests: result.data.map(item => ({
                ...item,
                content_item: new ReactiveContentItem(item.content_item),
            })),
            total: Math.ceil(result.meta.total / 10),
        };
    }, {
        revalidateIfStale: false,
        revalidateOnFocus: false,
    })

    const total = data?.requests.length;
    return <div className={containerClassName}>
        <div className={styles.title}>
            <span>Content Approval Requests</span> {isLoading || <>{total}</>}
        </div>
        {isLoading ? <div className={styles.loading}>
            <FontAwesome name={'fa-spinner'} spin prefix={'fas'}/>
        </div> : <div>
            <table className={styles.requestTable}>
                <colgroup>
                    <col/>
                    <col/>
                    <col/>
                    <col/>
                    <col/>
                </colgroup>
                <thead>
                    <tr>
                        <th>Title</th>
                        <th>Target Page</th>
                        <th>Initiator</th>
                        <th>Created</th>
                        <th>Actions</th>
                    </tr>
                </thead>
                <tbody>
                {data && data.requests.length > 0 ? <TransitionGroup component={null}>
                    {data.requests.map(d => {
                        const ref = createRef<HTMLTableRowElement>();
                        return <CSSTransition classNames={transitionClassNames} timeout={250} nodeRef={ref}>
                            <ApprovalItem
                                key={d.id}
                                ref={ref}
                                item={d}
                                boundMutate={mutate}
                            />
                        </CSSTransition>
                    })}
                </TransitionGroup> : <tr>
                    <td colSpan={5} style={{pointerEvents: "none"}}>No Pending Requests</td>
                </tr>}
                </tbody>
            </table>
            {data && <PaginationFooter
                currentPage={page}
                setPage={setPage}
                totalPages={data.total}
            />}
            <div className={styles.footerLink}>
            <ClickableLink title={"AutoApprove management"} aria-label={"AutoApprove management"}
                    className={styles.manageButton} onClick={() => {
                modalStore.addModal({
                    type: "autoApproveManagement",
                    id: "autoApproveManagement",
                })
            }}>
                View Auto-Approvals <div className={styles.tooltip} data-content={"Auto-Approvals currently enabled for pages you administrate"}><FontAwesome prefix={'far'} name={'fa-question-circle'} /></div>
            </ClickableLink>
            </div>
        </div>}
    </div>
}
